export type DimensionId = string;
export type RealityId = string;
export type MirrorId = string;
export type EndpointId = string;

export type DimensionalStage =
  | "dormant"
  | "reflection"
  | "anomaly"
  | "resonance"
  | "contact"
  | "synchronization"
  | "aperture"
  | "crossing"
  | "stabilization";

export type OverlapLevel = 0 | 1 | 2 | 3 | 4 | 5;

export interface DimensionalVec3 {
  x: number;
  y: number;
  z: number;
}

export interface DimensionDescriptor {
  id: DimensionId;
  label: string;
  timeScale: number;
  gravityScale: number;
  topology: "euclidean" | "folded" | "probabilistic";
}

export interface PortalEndpoint {
  id: EndpointId;
  dimensionId: DimensionId;
  realityId: RealityId;
  position: DimensionalVec3;
  yawRad?: number;
}

export interface DimensionalMirror {
  id: MirrorId;
  endpointA: EndpointId;
  endpointB: EndpointId;
  stage: DimensionalStage;
  resonance01: number;
  stability01: number;
  energy01: number;
  overlapLevel: OverlapLevel;
  lastInteractionAtMs: number;
  openedAtMs?: number;
}

export interface ConsciousnessIdentity {
  id: string;
  originRealityId: RealityId;
  createdAtMs: number;
}

export interface DimensionalManifestation {
  id: string;
  entityId: string;
  consciousnessId: string;
  dimensionId: DimensionId;
  realityId: RealityId;
  position: DimensionalVec3;
  velocity: DimensionalVec3;
  active: boolean;
  localMemory: string[];
}

export interface MirrorMemoryEntry {
  id: string;
  mirrorId: MirrorId;
  entityId: string;
  consciousnessId: string;
  fromEndpointId: EndpointId;
  toEndpointId: EndpointId;
  crossedAtMs: number;
  resonance01: number;
  stability01: number;
}

export interface ResonanceSample {
  atMs: number;
  observerEntityId: string;
  distanceM: number;
  gazeAlignment01?: number;
  contact?: boolean;
  intent?: boolean;
}

export interface CrossingTicket {
  id: string;
  mirrorId: MirrorId;
  manifestationId: string;
  entityId: string;
  consciousnessId: string;
  fromEndpointId: EndpointId;
  toEndpointId: EndpointId;
  createdAtMs: number;
  sourcePosition: DimensionalVec3;
  sourceVelocity: DimensionalVec3;
}

export type DimensionalEvent =
  | { type: "mirror-stage"; atMs: number; mirrorId: MirrorId; from: DimensionalStage; to: DimensionalStage }
  | { type: "resonance"; atMs: number; mirrorId: MirrorId; entityId: string; resonance01: number }
  | { type: "portal-opened"; atMs: number; mirrorId: MirrorId }
  | { type: "portal-collapsed"; atMs: number; mirrorId: MirrorId; reason: string }
  | { type: "crossing-started"; atMs: number; mirrorId: MirrorId; entityId: string; ticketId: string }
  | { type: "crossing-complete"; atMs: number; mirrorId: MirrorId; entityId: string; ticketId: string; dimensionId: DimensionId; realityId: RealityId };

export interface DimensionBridgeSnapshot {
  version: 1;
  capturedAtMs: number;
  dimensions: DimensionDescriptor[];
  dimensionClockMs: Array<[DimensionId, number]>;
  endpoints: PortalEndpoint[];
  mirrors: DimensionalMirror[];
  consciousness: ConsciousnessIdentity[];
  manifestations: DimensionalManifestation[];
  mirrorMemory: MirrorMemoryEntry[];
}

const STAGE_ORDER: DimensionalStage[] = [
  "dormant",
  "reflection",
  "anomaly",
  "resonance",
  "contact",
  "synchronization",
  "aperture",
  "crossing",
  "stabilization",
];

const clamp01 = (value: number): number => Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
const copyVec = (value: DimensionalVec3): DimensionalVec3 => ({ x: value.x, y: value.y, z: value.z });
const copyEndpoint = (value: PortalEndpoint): PortalEndpoint => ({ ...value, position: copyVec(value.position) });
const copyManifestation = (value: DimensionalManifestation): DimensionalManifestation => ({
  ...value,
  position: copyVec(value.position),
  velocity: copyVec(value.velocity),
  localMemory: [...value.localMemory],
});

function overlapForStage(stage: DimensionalStage): OverlapLevel {
  switch (stage) {
    case "dormant": return 0;
    case "reflection": return 1;
    case "anomaly": return 2;
    case "resonance": return 2;
    case "contact": return 3;
    case "synchronization": return 4;
    case "aperture":
    case "crossing":
    case "stabilization":
      return 5;
  }
}

function stageForResonance(resonance01: number, contact: boolean): DimensionalStage {
  if (resonance01 < 0.08) return "dormant";
  if (resonance01 < 0.22) return "reflection";
  if (resonance01 < 0.38) return "anomaly";
  if (resonance01 < 0.58) return "resonance";
  if (!contact) return "resonance";
  if (resonance01 < 0.76) return "contact";
  if (resonance01 < 0.86) return "synchronization";
  return "aperture";
}

function rotateXZ(value: DimensionalVec3, radians: number): DimensionalVec3 {
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  return {
    x: value.x * c - value.z * s,
    y: value.y,
    z: value.x * s + value.z * c,
  };
}

/**
 * DimensionBridgeCore is simulation-only infrastructure.
 *
 * It intentionally has no browser/device/network/RealityBridge dependency.
 * Host capabilities remain outside this model so a virtual dimensional crossing
 * can never become implicit permission to act on the user's machine.
 */
export class DimensionBridgeCore {
  private readonly dimensions = new Map<DimensionId, DimensionDescriptor>();
  private readonly dimensionClockMs = new Map<DimensionId, number>();
  private readonly endpoints = new Map<EndpointId, PortalEndpoint>();
  private readonly mirrors = new Map<MirrorId, DimensionalMirror>();
  private readonly consciousness = new Map<string, ConsciousnessIdentity>();
  private readonly manifestations = new Map<string, DimensionalManifestation>();
  private readonly mirrorMemory: MirrorMemoryEntry[] = [];
  private readonly listeners = new Set<(event: DimensionalEvent) => void>();
  private ticketSequence = 0;
  private memorySequence = 0;

  registerDimension(input: DimensionDescriptor): DimensionDescriptor {
    if (!input.id) throw new Error("dimension id is required");
    if (!(input.timeScale > 0)) throw new Error("dimension timeScale must be positive");
    if (!(input.gravityScale >= 0)) throw new Error("dimension gravityScale must be non-negative");
    const existing = this.dimensions.get(input.id);
    if (existing) return { ...existing };
    const dimension = { ...input };
    this.dimensions.set(input.id, dimension);
    this.dimensionClockMs.set(input.id, 0);
    return { ...dimension };
  }

  registerMirror(input: {
    id: MirrorId;
    endpointA: PortalEndpoint;
    endpointB: PortalEndpoint;
    stability01?: number;
    energy01?: number;
  }): DimensionalMirror {
    if (this.mirrors.has(input.id)) return this.getMirror(input.id);
    this.requireDimension(input.endpointA.dimensionId);
    this.requireDimension(input.endpointB.dimensionId);
    if (input.endpointA.id === input.endpointB.id) throw new Error("mirror endpoints must be distinct");
    if (this.endpoints.has(input.endpointA.id) || this.endpoints.has(input.endpointB.id)) {
      throw new Error("portal endpoint id already registered");
    }
    this.endpoints.set(input.endpointA.id, copyEndpoint(input.endpointA));
    this.endpoints.set(input.endpointB.id, copyEndpoint(input.endpointB));
    const mirror: DimensionalMirror = {
      id: input.id,
      endpointA: input.endpointA.id,
      endpointB: input.endpointB.id,
      stage: "dormant",
      resonance01: 0,
      stability01: clamp01(input.stability01 ?? 1),
      energy01: clamp01(input.energy01 ?? 1),
      overlapLevel: 0,
      lastInteractionAtMs: 0,
    };
    this.mirrors.set(mirror.id, mirror);
    return { ...mirror };
  }

  setEndpointPosition(endpointId: EndpointId, position: DimensionalVec3): void {
    const endpoint = this.requireEndpoint(endpointId);
    endpoint.position = copyVec(position);
  }

  endpoint(endpointId: EndpointId): PortalEndpoint {
    return copyEndpoint(this.requireEndpoint(endpointId));
  }

  registerConsciousness(input: ConsciousnessIdentity): ConsciousnessIdentity {
    const existing = this.consciousness.get(input.id);
    if (existing) return { ...existing };
    const identity = { ...input };
    this.consciousness.set(input.id, identity);
    return { ...identity };
  }

  registerManifestation(input: DimensionalManifestation): DimensionalManifestation {
    this.requireDimension(input.dimensionId);
    const identity = this.consciousness.get(input.consciousnessId);
    if (!identity) throw new Error("consciousness must be registered before manifestation");
    const existing = this.manifestations.get(input.id);
    if (existing) return copyManifestation(existing);
    const manifestation = copyManifestation(input);
    this.manifestations.set(manifestation.id, manifestation);
    return copyManifestation(manifestation);
  }

  syncManifestation(manifestationId: string, patch: {
    position?: DimensionalVec3;
    velocity?: DimensionalVec3;
  }): DimensionalManifestation {
    const manifestation = this.requireManifestation(manifestationId);
    if (patch.position) manifestation.position = copyVec(patch.position);
    if (patch.velocity) manifestation.velocity = copyVec(patch.velocity);
    return copyManifestation(manifestation);
  }

  getMirror(mirrorId: MirrorId): DimensionalMirror {
    const mirror = this.mirrors.get(mirrorId);
    if (!mirror) throw new Error("unknown dimensional mirror: " + mirrorId);
    return { ...mirror };
  }

  getManifestation(manifestationId: string): DimensionalManifestation {
    return copyManifestation(this.requireManifestation(manifestationId));
  }

  manifestationsForConsciousness(consciousnessId: string): DimensionalManifestation[] {
    return [...this.manifestations.values()]
      .filter(item => item.consciousnessId === consciousnessId)
      .map(copyManifestation);
  }

  onEvent(listener: (event: DimensionalEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  resonate(mirrorId: MirrorId, sample: ResonanceSample): DimensionalMirror {
    const mirror = this.requireMirror(mirrorId);
    const proximity = 1 - clamp01(sample.distanceM / 10);
    const gaze = clamp01(sample.gazeAlignment01 ?? 0.5);
    const contactBoost = sample.contact ? 0.2 : 0;
    const intentBoost = sample.intent ? 0.16 : 0;
    const signal = clamp01(proximity * 0.52 + gaze * 0.2 + contactBoost + intentBoost);
    const freshness = mirror.lastInteractionAtMs <= 0
      ? 1
      : clamp01(1 - Math.max(0, sample.atMs - mirror.lastInteractionAtMs) / 12000);
    mirror.resonance01 = clamp01(
      mirror.resonance01 * (0.68 + freshness * 0.12) + signal * (0.32 + (1 - freshness) * 0.12),
    );
    mirror.lastInteractionAtMs = sample.atMs;

    if (mirror.stage !== "aperture" && mirror.stage !== "crossing" && mirror.stage !== "stabilization") {
      this.transitionStage(mirror, stageForResonance(mirror.resonance01, Boolean(sample.contact)), sample.atMs);
    }
    mirror.overlapLevel = overlapForStage(mirror.stage);
    this.emit({
      type: "resonance",
      atMs: sample.atMs,
      mirrorId,
      entityId: sample.observerEntityId,
      resonance01: mirror.resonance01,
    });
    return { ...mirror };
  }

  requestOpen(mirrorId: MirrorId, atMs: number): DimensionalMirror {
    const mirror = this.requireMirror(mirrorId);
    if (mirror.resonance01 < 0.72) throw new Error("mirror resonance is below aperture threshold");
    if (mirror.stability01 < 0.35) throw new Error("mirror stability is too low");
    if (mirror.energy01 < 0.2) throw new Error("mirror energy is too low");
    this.transitionStage(mirror, "aperture", atMs);
    mirror.openedAtMs = atMs;
    mirror.overlapLevel = 5;
    this.emit({ type: "portal-opened", atMs, mirrorId });
    return { ...mirror };
  }

  collapse(mirrorId: MirrorId, atMs: number, reason = "manual"): void {
    const mirror = this.requireMirror(mirrorId);
    this.transitionStage(mirror, "dormant", atMs);
    mirror.resonance01 = 0;
    mirror.overlapLevel = 0;
    mirror.openedAtMs = undefined;
    this.emit({ type: "portal-collapsed", atMs, mirrorId, reason });
  }

  beginCrossing(input: {
    mirrorId: MirrorId;
    entityId: string;
    fromEndpointId: EndpointId;
    atMs: number;
  }): CrossingTicket {
    const mirror = this.requireMirror(input.mirrorId);
    if (mirror.stage !== "aperture" && mirror.stage !== "stabilization") {
      throw new Error("mirror is not open for crossing");
    }
    const from = this.requireEndpoint(input.fromEndpointId);
    const toEndpointId = mirror.endpointA === from.id ? mirror.endpointB
      : mirror.endpointB === from.id ? mirror.endpointA
      : "";
    if (!toEndpointId) throw new Error("source endpoint does not belong to mirror");
    const manifestation = [...this.manifestations.values()].find(item =>
      item.entityId === input.entityId &&
      item.dimensionId === from.dimensionId &&
      item.realityId === from.realityId &&
      item.active
    );
    if (!manifestation) throw new Error("active source manifestation not found");
    this.transitionStage(mirror, "crossing", input.atMs);
    const ticket: CrossingTicket = {
      id: "crossing:" + input.mirrorId + ":" + (++this.ticketSequence),
      mirrorId: input.mirrorId,
      manifestationId: manifestation.id,
      entityId: manifestation.entityId,
      consciousnessId: manifestation.consciousnessId,
      fromEndpointId: from.id,
      toEndpointId,
      createdAtMs: input.atMs,
      sourcePosition: copyVec(manifestation.position),
      sourceVelocity: copyVec(manifestation.velocity),
    };
    this.emit({
      type: "crossing-started",
      atMs: input.atMs,
      mirrorId: input.mirrorId,
      entityId: input.entityId,
      ticketId: ticket.id,
    });
    return ticket;
  }

  completeCrossing(ticket: CrossingTicket, atMs: number): DimensionalManifestation {
    const mirror = this.requireMirror(ticket.mirrorId);
    const from = this.requireEndpoint(ticket.fromEndpointId);
    const to = this.requireEndpoint(ticket.toEndpointId);
    const manifestation = this.requireManifestation(ticket.manifestationId);
    if (manifestation.entityId !== ticket.entityId || manifestation.consciousnessId !== ticket.consciousnessId) {
      throw new Error("crossing ticket identity mismatch");
    }
    const sourceYaw = from.yawRad ?? 0;
    const targetYaw = to.yawRad ?? 0;
    const deltaYaw = targetYaw - sourceYaw + Math.PI;
    const localOffset = {
      x: ticket.sourcePosition.x - from.position.x,
      y: ticket.sourcePosition.y - from.position.y,
      z: ticket.sourcePosition.z - from.position.z,
    };
    const rotatedOffset = rotateXZ(localOffset, deltaYaw);
    const rotatedVelocity = rotateXZ(ticket.sourceVelocity, deltaYaw);
    manifestation.dimensionId = to.dimensionId;
    manifestation.realityId = to.realityId;
    manifestation.position = {
      x: to.position.x + rotatedOffset.x,
      y: to.position.y + rotatedOffset.y,
      z: to.position.z + rotatedOffset.z,
    };
    manifestation.velocity = rotatedVelocity;
    manifestation.localMemory.push(
      "crossed:" + ticket.mirrorId + ":" + ticket.fromEndpointId + ">" + ticket.toEndpointId,
    );
    if (manifestation.localMemory.length > 64) manifestation.localMemory.splice(0, manifestation.localMemory.length - 64);
    const memory: MirrorMemoryEntry = {
      id: "mirror-memory:" + (++this.memorySequence),
      mirrorId: ticket.mirrorId,
      entityId: ticket.entityId,
      consciousnessId: ticket.consciousnessId,
      fromEndpointId: ticket.fromEndpointId,
      toEndpointId: ticket.toEndpointId,
      crossedAtMs: atMs,
      resonance01: mirror.resonance01,
      stability01: mirror.stability01,
    };
    this.mirrorMemory.push(memory);
    if (this.mirrorMemory.length > 2048) this.mirrorMemory.splice(0, this.mirrorMemory.length - 2048);
    this.transitionStage(mirror, "stabilization", atMs);
    mirror.energy01 = clamp01(mirror.energy01 - 0.08);
    this.emit({
      type: "crossing-complete",
      atMs,
      mirrorId: ticket.mirrorId,
      entityId: ticket.entityId,
      ticketId: ticket.id,
      dimensionId: manifestation.dimensionId,
      realityId: manifestation.realityId,
    });
    return copyManifestation(manifestation);
  }

  tick(deltaMs: number, atMs: number): void {
    const dt = Math.max(0, Math.min(deltaMs, 60000));
    for (const dimension of this.dimensions.values()) {
      const clock = this.dimensionClockMs.get(dimension.id) ?? 0;
      this.dimensionClockMs.set(dimension.id, clock + dt * dimension.timeScale);
    }
    for (const mirror of this.mirrors.values()) {
      const open = mirror.stage === "aperture" || mirror.stage === "crossing" || mirror.stage === "stabilization";
      if (open) {
        mirror.energy01 = clamp01(mirror.energy01 - dt / 240000);
        if (mirror.energy01 <= 0.001) this.collapse(mirror.id, atMs, "energy-depleted");
        continue;
      }
      mirror.energy01 = clamp01(mirror.energy01 + dt / 900000);
      const idleMs = Math.max(0, atMs - mirror.lastInteractionAtMs);
      if (idleMs > 2500 && mirror.resonance01 > 0) {
        mirror.resonance01 = clamp01(mirror.resonance01 - dt / 30000);
        const next = stageForResonance(mirror.resonance01, false);
        if (STAGE_ORDER.indexOf(next) < STAGE_ORDER.indexOf(mirror.stage)) {
          this.transitionStage(mirror, next, atMs);
        }
        mirror.overlapLevel = overlapForStage(mirror.stage);
      }
    }
  }

  dimensionTimeMs(dimensionId: DimensionId): number {
    this.requireDimension(dimensionId);
    return this.dimensionClockMs.get(dimensionId) ?? 0;
  }

  mirrorHistory(mirrorId?: MirrorId): MirrorMemoryEntry[] {
    return this.mirrorMemory
      .filter(entry => !mirrorId || entry.mirrorId === mirrorId)
      .map(entry => ({ ...entry }));
  }

  snapshot(capturedAtMs = Date.now()): DimensionBridgeSnapshot {
    return {
      version: 1,
      capturedAtMs,
      dimensions: [...this.dimensions.values()].map(item => ({ ...item })),
      dimensionClockMs: [...this.dimensionClockMs.entries()],
      endpoints: [...this.endpoints.values()].map(copyEndpoint),
      mirrors: [...this.mirrors.values()].map(item => ({ ...item })),
      consciousness: [...this.consciousness.values()].map(item => ({ ...item })),
      manifestations: [...this.manifestations.values()].map(copyManifestation),
      mirrorMemory: this.mirrorMemory.map(item => ({ ...item })),
    };
  }

  restore(snapshot: DimensionBridgeSnapshot, atMs = Date.now()): void {
    if (snapshot.version !== 1) throw new Error("unsupported dimensional snapshot version");
    this.dimensions.clear();
    this.dimensionClockMs.clear();
    this.endpoints.clear();
    this.mirrors.clear();
    this.consciousness.clear();
    this.manifestations.clear();
    this.mirrorMemory.splice(0);

    for (const dimension of snapshot.dimensions) this.registerDimension(dimension);
    for (const [dimensionId, clock] of snapshot.dimensionClockMs) {
      if (this.dimensions.has(dimensionId)) this.dimensionClockMs.set(dimensionId, Math.max(0, clock));
    }
    for (const endpoint of snapshot.endpoints) this.endpoints.set(endpoint.id, copyEndpoint(endpoint));
    for (const mirror of snapshot.mirrors) this.mirrors.set(mirror.id, { ...mirror });
    for (const identity of snapshot.consciousness) this.consciousness.set(identity.id, { ...identity });
    for (const manifestation of snapshot.manifestations) this.manifestations.set(manifestation.id, copyManifestation(manifestation));
    this.mirrorMemory.push(...snapshot.mirrorMemory.map(item => ({ ...item })));

    const offlineMs = Math.max(0, Math.min(atMs - snapshot.capturedAtMs, 7 * 24 * 60 * 60 * 1000));
    if (offlineMs > 0) {
      for (const dimension of this.dimensions.values()) {
        const clock = this.dimensionClockMs.get(dimension.id) ?? 0;
        this.dimensionClockMs.set(dimension.id, clock + offlineMs * dimension.timeScale);
      }
      for (const mirror of this.mirrors.values()) {
        if (mirror.stage === "aperture" || mirror.stage === "crossing" || mirror.stage === "stabilization") {
          mirror.stage = "reflection";
          mirror.overlapLevel = 1;
          mirror.openedAtMs = undefined;
        }
        mirror.energy01 = clamp01(mirror.energy01 + offlineMs / 900000);
        mirror.resonance01 = clamp01(mirror.resonance01 - offlineMs / 30000);
      }
    }
  }

  private transitionStage(mirror: DimensionalMirror, next: DimensionalStage, atMs: number): void {
    if (mirror.stage === next) return;
    const previous = mirror.stage;
    mirror.stage = next;
    mirror.overlapLevel = overlapForStage(next);
    this.emit({ type: "mirror-stage", atMs, mirrorId: mirror.id, from: previous, to: next });
  }

  private emit(event: DimensionalEvent): void {
    for (const listener of [...this.listeners]) listener(event);
  }

  private requireDimension(id: DimensionId): DimensionDescriptor {
    const value = this.dimensions.get(id);
    if (!value) throw new Error("unknown dimension: " + id);
    return value;
  }

  private requireEndpoint(id: EndpointId): PortalEndpoint {
    const value = this.endpoints.get(id);
    if (!value) throw new Error("unknown portal endpoint: " + id);
    return value;
  }

  private requireMirror(id: MirrorId): DimensionalMirror {
    const value = this.mirrors.get(id);
    if (!value) throw new Error("unknown dimensional mirror: " + id);
    return value;
  }

  private requireManifestation(id: string): DimensionalManifestation {
    const value = this.manifestations.get(id);
    if (!value) throw new Error("unknown dimensional manifestation: " + id);
    return value;
  }
}
